/**
 * Normalizes coordinates to a standard {lat, lng} format with numeric values
 * Handles Firestore GeoPoint objects, custom formats, and nested structures
 * @param {Object} coords The coordinates to normalize
 * @returns {Object|null} Normalized coordinates or null if invalid
 */
export const normalizeCoordinates = (coords) => {
  if (!coords) {
    console.warn('[locationUtils] normalizeCoordinates: null or undefined coordinates');
    return null;
  }
  
  // Extract latitude and longitude values, checking various potential formats
  let lat, lng;
  
  // If coords is a Firestore GeoPoint-like object
  if (typeof coords.latitude === 'number' && typeof coords.longitude === 'number') {
    lat = coords.latitude;
    lng = coords.longitude;
    console.log('[locationUtils] Detected Firestore GeoPoint format:', { lat, lng });
  }
  // If coords uses standard lat/lng format
  else if (typeof coords.lat === 'number' && typeof coords.lng === 'number') {
    lat = coords.lat;
    lng = coords.lng;
    console.log('[locationUtils] Detected standard lat/lng format:', { lat, lng });
  }
  // Handle string or non-numeric values for lat/lng
  else if ('lat' in coords && 'lng' in coords) {
    lat = Number(coords.lat);
    lng = Number(coords.lng);
    console.log('[locationUtils] Converted string lat/lng format to numbers:', { lat, lng });
  }
  // Handle string or non-numeric values for latitude/longitude
  else if ('latitude' in coords && 'longitude' in coords) {
    lat = Number(coords.latitude);
    lng = Number(coords.longitude);
    console.log('[locationUtils] Converted string latitude/longitude format to numbers:', { lat, lng });
  }
  // Handle nested coordinates structure (from various sources)
  else if (coords.coordinates && typeof coords.coordinates === 'object') {
    const nested = coords.coordinates;
    if ('lat' in nested || 'latitude' in nested) {
      lat = Number(nested.lat || nested.latitude);
      lng = Number(nested.lng || nested.longitude);
      console.log('[locationUtils] Extracted from nested structure:', { lat, lng });
    }
  }
  // No recognized format found
  else {
    console.warn('[locationUtils] normalizeCoordinates: Unrecognized coordinate format:', coords);
    return null;
  }
  
  // Ensure coordinates are numbers
  const numLat = Number(lat);
  const numLng = Number(lng);
  
  // Validate that the values are actual numbers
  if (isNaN(numLat) || isNaN(numLng)) {
    console.warn('[locationUtils] normalizeCoordinates: Invalid coordinate values:', { lat, lng });
    return null;
  }
  
  // Return the normalized format with numeric values
  return {
    lat: numLat,
    lng: numLng
  };
};

/**
 * Validates and formats coordinates
 * @param {Object} coordinates The coordinates to validate
 * @returns {Object|null} Formatted coordinates if valid, null if invalid
 */
export const validateCoordinates = (coordinates) => {
  // First normalize the coordinates to standard format with numeric values
  const normalized = normalizeCoordinates(coordinates);
  if (!normalized) {
    console.warn('[locationUtils] validateCoordinates: Could not normalize coordinates:', coordinates);
    return null;
  }

  const lat = normalized.lat; // Already a number from normalizeCoordinates
  const lng = normalized.lng; // Already a number from normalizeCoordinates

  // Validate coordinate ranges
  if (lat < -90 || lat > 90) {
    console.warn(`[locationUtils] validateCoordinates: Latitude ${lat} out of range (-90 to 90)`);
    return null;
  }
  
  if (lng < -180 || lng > 180) {
    console.warn(`[locationUtils] validateCoordinates: Longitude ${lng} out of range (-180 to 180)`);
    return null;
  }

  // Return validated coordinates as numbers to ensure consistency
  return { 
    lat: Number(lat), // Explicit Number conversion for consistency
    lng: Number(lng)  // Explicit Number conversion for consistency
  };
};

/**
 * Debug function to log coordinate information
 * @param {string} context A string identifying where this is being called from
 * @param {Object} original The original coordinates object
 * @param {Object} normalized The normalized coordinates (optional)
 * @param {Object} validated The validated coordinates (optional)
 */
export const debugCoordinates = (context, original, normalized = null, validated = null) => {
  console.log(`[${context}] Coordinate debug:`, {
    original,
    normalized,
    validated,
    originalType: original ? typeof original : 'undefined',
    originalIsObject: original ? typeof original === 'object' : false,
    hasLatitude: original?.latitude !== undefined,
    hasLongitude: original?.longitude !== undefined,
    hasLat: original?.lat !== undefined,
    hasLng: original?.lng !== undefined,
    nestedCoords: original?.coordinates ? 'Yes' : 'No'
  });
};

/**
 * Type definition for coordinates
 * @typedef {Object} Coordinates
 * @property {number} lat - Latitude between -90 and 90
 * @property {number} lng - Longitude between -180 and 180
 */
